import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Ma manivelle de volet roulant tourne dans le vide, que faire ?', a: 'C\'est le signe d\'un problème au niveau du cardan (la pièce qui relie la manivelle au mécanisme) ou du réducteur. Ne continuez pas à tourner dans le vide, vous risqueriez d\'aggraver les dégâts. Contactez-nous pour un diagnostic et une réparation.' },
  { q: 'Peut-on remplacer une manivelle par une motorisation électrique ?', a: 'Oui, absolument. Le passage d\'un volet à manivelle à un volet motorisé est une opération courante. Nous retirons le mécanisme à manivelle et installons un moteur dans le caisson. C\'est une excellente solution pour gagner en confort au quotidien.' },
];

export default function Manivelle() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Réparation manivelle de volet roulant à Lyon | DFPRO"
        description="Manivelle de volet roulant bloquée ou cassée à Lyon ? Réparation cardan, treuil, sortie de caisson. Devis gratuit, intervention rapide."
        canonicalUrl="https://depanfenetres-lyon.fr/manivelle-volet-roulant-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-manivelle-2026.jpg" alt="Réparation de manivelle et treuil de volet roulant à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Manivelle et treuil</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Réparation de manivelle et treuil de volet roulant à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Manivelle dure, bloquée, ou qui tourne dans le vide ? Nos techniciens réparent le mécanisme de votre volet roulant manuel à Lyon et dans la métropole.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Les pannes de manivelle et treuil de volet roulant</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le système à manivelle équipe de nombreux volets roulants, notamment dans les habitations construites avant les années 2000. Bien que robuste, ce mécanisme peut présenter des signes de fatigue avec le temps. Les pannes les plus courantes concernent la manivelle elle-même, le cardan (la pièce de liaison), et le treuil (le mécanisme d'enroulement dans le caisson).
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Une manivelle qui devient dure à tourner est souvent le signe d'un manque de lubrification ou d'une usure du réducteur. Si la manivelle tourne dans le vide sans que le volet ne bouge, le cardan est probablement cassé ou désolidarisé du treuil. Enfin, si le volet descend tout seul ou ne tient pas en position, c'est le frein du treuil qui est en cause.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Nos techniciens connaissent parfaitement ces mécanismes et interviennent avec les pièces nécessaires pour remettre votre volet en état de marche. Dans la majorité des cas, la réparation peut être effectuée en moins d'une heure.
                  </p>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/changer-sangle-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Changement de sangle</Link>
                    <Link to="/motorisation-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Motorisation volet roulant</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — manivelle et treuil</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Manivelle de volet roulant en panne à Lyon ?" subtitle="Réparation rapide du cardan et du treuil. Devis gratuit." />
      </main>
      <Footer />
    </>
  );
}